import React from "react";
import { PricingRecordAction } from "./pricingRecordReducer";
import {
  IPricingRecord,
  IPricingRecordLineItem,
} from "@services/QuoteBuilder/QuoteBuilderService";
import DropDownMenu, { IChoice } from "@components/inputs/DropDownMenu";
import { PanelItem } from "@components/panels/PanelItems";

type Props = {
  name: string;
  pricingRecordState: IPricingRecord | null;
  pricingRecordDispatch: React.Dispatch<PricingRecordAction>;
  optionList: string[];
  noneOption?: IChoice;
  required?: boolean;
  disabled?: boolean;

  /* Added `includeAllOption` to support a 1:many dropdown where one entry
  controls the inclusion/exclusion of multiple line items together.
  If `includeAllOption` is provided, the dropdown includes an additional
  entry (e.g., "Included") that toggles all line items in `optionList`.
  Future: For multiple 1:many dropdowns, consider creating a new component
  (e.g., LinkedPlanOptionComponent) to handle multiple linked groups.
  Separating the 1-many gives a clean separation of concerns and avoids
  complicating the logic of this existing component. */
  includeAllOption?: { label: string };
};

export default function PlanOptionComponent({
  name,
  pricingRecordState,
  pricingRecordDispatch,
  optionList,
  noneOption,
  required = false,
  disabled = false,
  includeAllOption,
}: Props) {
  let optionLineItems: IPricingRecordLineItem[] = [];
  let selectOption: string = "";
  let dropDownOptions: IChoice[] = [];

  if (pricingRecordState?.pricing_record_line_items) {
    optionLineItems = pricingRecordState.pricing_record_line_items.filter(
      (item: IPricingRecordLineItem) =>
        optionList.includes(item.chargebee_component)
    );

    let localDropDownOptions: IChoice[] = [];
    if (noneOption) {
      localDropDownOptions = [{ ...noneOption, value: "" }];
    }

    if (includeAllOption) {
      // 1:many — single dropdown entry for all items
      localDropDownOptions.push({
        label: includeAllOption.label,
        value: "include-all",
        enabled: true,
      });

      if (optionLineItems.some((item) => item.included)) {
        selectOption = "include-all";
      }
    } else {
      // 1:1 — one dropdown entry per item
      optionLineItems.forEach((lineItem) => {
        if (lineItem.included) {
          selectOption = lineItem.pricing_template_line_item_id.toString();
        }

        localDropDownOptions = localDropDownOptions.concat({
          label: lineItem.line_item_label_value
            ? lineItem.line_item_label_value
            : undefined,
          value: lineItem.pricing_template_line_item_id.toString(),
          enabled: true,
        });
      });
    }

    dropDownOptions = localDropDownOptions;
  }

  const onSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.currentTarget.value;

    if (includeAllOption) {
      // 1:many — include or exclude ALL optionList items together
      const type = selectedValue === "include-all"
        ? "INCLUDE_PRICING_RECORD_DETAIL"
        : "EXCLUDE_PRICING_RECORD_DETAIL";

      optionLineItems.forEach((lineItem) => {
        pricingRecordDispatch({ type, payload: lineItem.pricing_template_line_item_id });
      });
    } else {
      // 1:1 — original per-item logic
      optionLineItems.forEach((lineItem) => {
        if (lineItem.pricing_template_line_item_id.toString() == selectedValue) {
          pricingRecordDispatch({
            type: "INCLUDE_PRICING_RECORD_DETAIL",
            payload: lineItem.pricing_template_line_item_id,
          });
        } else {
          pricingRecordDispatch({
            type: "EXCLUDE_PRICING_RECORD_DETAIL",
            payload: lineItem.pricing_template_line_item_id,
          });
        }
      });
    }
  };

  return (
    <>
      <div style={{ margin: "1vh 0 2.5vh 0" }} data-testid={name}>
        <DropDownMenu
          menuName={name}
          required={required}
          choices={dropDownOptions}
          defaultVal={{ value: selectOption }}
          onSelect={onSelect}
          disabled={disabled}
        />
      </div>

      {optionLineItems
        .filter((item) => item.included)
        .map((item, index: number) => (
          <PanelItem
            key={index}
            header={item.line_item_label_name}
            value={item.price_including_gst.toFixed(2)}
            headerTextStyleCfg={{ lineBreak: true }}
            testId={`${name}-selected-${item.line_item_label_name}`}
          />
        ))}
    </>
  );
}
